// camelCase to snake_case helper type
type StringToSnakeCase<T extends string> = T extends `${infer Head}${infer Tail}`
  ? `${Head extends Capitalize<Head> ? '_' : ''}${Lowercase<Head>}${StringToSnakeCase<Tail>}`
  : T

// camelCase to snake_case type
export type CamelToSnakeCase<T> = T extends object
  ? {
      [K in keyof T as K extends string ? StringToSnakeCase<K> : K]: CamelToSnakeCase<T[K]>
    }
  : T

// camelCase to snake_case helper function
const stringToSnakeCase = (s: string): string => {
  // Special case for all caps acronyms like "API"
  if (/^[A-Z]+$/.test(s)) return s.toLowerCase()

  // Handle leading underscore
  const hasLeadingUnderscore = s.startsWith('_')
  const baseString = hasLeadingUnderscore ? s.substring(1) : s

  // First handle camelCase transitions (lower to upper)
  // Then handle acronyms (sequences of uppercase letters followed by lowercase)
  // Then convert to lowercase
  const result = baseString
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase()

  return hasLeadingUnderscore ? `_${result}` : result
}

// camelCase to snake_case function
export const camelToSnakeCase = <T>(a: T): CamelToSnakeCase<T> => {
  if (typeof a === 'string') return stringToSnakeCase(a) as CamelToSnakeCase<T>
  if (a === null || typeof a !== 'object') return a as CamelToSnakeCase<T>
  if (Array.isArray(a)) return a.map(camelToSnakeCase) as CamelToSnakeCase<T>

  return Object.entries(a as object).reduce((result, [k, v]) => {
    const newKey = stringToSnakeCase(k)
    const newValue = v !== null && typeof v === 'object' ? camelToSnakeCase(v) : v

    return { ...result, [newKey]: newValue }
  }, {}) as CamelToSnakeCase<T>
}

// snake_case to camelCase helper type
type StringToCamelCase<T extends string> = T extends `${infer Head}_${infer Tail}`
  ? `${Head}${Capitalize<StringToCamelCase<Tail>>}`
  : T

// snake_case to camelCase type
export type SnakeToCamelCase<T> = T extends object
  ? {
      [K in keyof T as K extends string ? StringToCamelCase<K> : K]: SnakeToCamelCase<T[K]>
    }
  : T

// snake_case to camelCase helper function
const stringToCamelCase = (s: string): string => {
  // Preserve leading underscores
  const hasLeadingUnderscore = s.startsWith('_')
  const baseString = hasLeadingUnderscore ? s.substring(1) : s

  const camelized = baseString.replace(/_([a-zA-Z0-9])/g, (_, letter) => letter.toUpperCase())

  return hasLeadingUnderscore ? `_${camelized}` : camelized
}

// snake_case to camelCase function
export const snakeToCamelCase = <T>(a: T): SnakeToCamelCase<T> => {
  if (typeof a === 'string') return stringToCamelCase(a) as SnakeToCamelCase<T>
  if (a === null || typeof a !== 'object') return a as SnakeToCamelCase<T>
  if (Array.isArray(a)) return a.map(snakeToCamelCase) as SnakeToCamelCase<T>

  return Object.entries(a as object).reduce((result, [k, v]) => {
    const newKey = stringToCamelCase(k)
    const newValue = v !== null && typeof v === 'object' ? snakeToCamelCase(v) : v

    return { ...result, [newKey]: newValue }
  }, {}) as SnakeToCamelCase<T>
}
